import type { SchoolBandsPagedResult } from '@/features/reports/api/schoolBands.dto';
import { useSchoolBandsQuery } from '@/features/reports/hooks/useSchoolBandsQuery';

/**
 * Whether school bands exist for an academic year (same signal as Manage Classification “Bands on server”).
 * Used to gate reports until a classification workflow exists for that year.
 */
export type SchoolBandsConfiguredForYearResult = {
  data: SchoolBandsPagedResult | undefined;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
  yearValid: boolean;
  configured: boolean;
  notConfigured: boolean;
  isChecking: boolean;
};

export function useSchoolBandsConfiguredForYear(
  yearStr: string | undefined | null
): SchoolBandsConfiguredForYearResult {
  const y = yearStr?.trim() || undefined;
  const yearValid = Boolean(y && /^\d+$/.test(y));
  const q = useSchoolBandsQuery(y);

  const configured = yearValid && q.isSuccess && (q.data?.totalCount ?? 0) > 0;
  const notConfigured = yearValid && q.isSuccess && (q.data?.totalCount ?? 0) === 0;
  const isChecking = yearValid && q.isPending;

  return {
    data: q.data,
    isPending: q.isPending,
    isSuccess: q.isSuccess,
    isError: q.isError,
    error: q.error instanceof Error ? q.error : q.error != null ? new Error(String(q.error)) : null,
    yearValid,
    configured,
    notConfigured,
    isChecking,
  };
}
